import React from "react";
import { pipe } from "fp-ts/function";
import * as A from "fp-ts/Array";
import * as O from "fp-ts/Option";
import * as R from "fp-ts/Record";
import { match } from "ts-pattern";

import { StarIcon } from "src/assets/icons/star_icon";
import HandsImg from "src/assets/images/domain-hands.png";
import HeadImg from "src/assets/images/domain-head.png";
import HeartImg from "src/assets/images/domain-heart.png";
import IntellectualImg from "src/assets/images/domain-intellectual.png";
import NurtureImg from "src/assets/images/domain-nurture.png";
import PracticalImg from "src/assets/images/domain-practical.png";
import { Separator } from "src/components/separator/separator";
import { Body, PageTitle, SectionTitle } from "src/components/typography/typography";
import { FFIBrandedV1Report } from "src/pages/reports/ffi/v1/types";
import { isBenchmarkRanked } from "src/pages/reports/ranking";

import { meta } from "./meta";

/*
 * Types
 */

export type MeaningfulStrengthsProps = {
    readonly report: FFIBrandedV1Report;
};

/*
 * Main
 */

export const MeaningfulStrengths = (props: MeaningfulStrengthsProps): React.ReactElement => {
    const { report } = props;
    const benchmarkRanked = isBenchmarkRanked(report.value.results);

    return (
        <div className="main-report-content-wrap">
            <PageTitle className="gold-icon" style={{ color: "#99b84e" }}>
                <StarIcon />
                Meaningful Strengths
            </PageTitle>
            {benchmarkRanked ? (
                <Body>
                    Below are up to five strengths of your school in regard to supporting spiritual formation and
                    biblical worldview development: the areas where your school’s score is highest relative to the FFI
                    benchmark. Your school’s meaningful strengths are areas to affirm, share, and celebrate with your
                    school community as well as external audiences.
                </Body>
            ) : (
                <Body>
                    Below are five strengths of your school in regard to supporting spiritual formation and biblical
                    worldview development. Your school’s meaningful strengths are areas to affirm, share, and celebrate
                    with your school community as well as external audiences.
                </Body>
            )}
            <Separator />
            <div className="sub-item-wrap">
                {pipe(
                    report.value.results.meaningfulStrengths,
                    A.map(({ domain, construct }) => {
                        return (
                            <div className="an-opportunity-item s-and-g-opp" key={construct}>
                                <img
                                    alt={`${domain} key growth opportunity domain`}
                                    className="domain-image"
                                    height="100px"
                                    src={match(domain)
                                        .with("intellectual", () => IntellectualImg)
                                        .with("nurture", () => NurtureImg)
                                        .with("practical", () => PracticalImg)
                                        .with("head", () => HeadImg)
                                        .with("hands", () => HandsImg)
                                        .with("heart", () => HeartImg)
                                        .exhaustive()}
                                />
                                {pipe(
                                    R.lookup(domain, meta),
                                    O.chain(R.lookup(construct)),
                                    O.map((m) => {
                                        return (
                                            <>
                                                <SectionTitle style={{ color: "#99b84e" }}>{m.title}</SectionTitle>
                                                <Body>{m.prologue}</Body>
                                            </>
                                        );
                                    }),
                                    O.getOrElse(() => {
                                        return (
                                            <>
                                                <SectionTitle style={{ color: "#99b84e" }}>Unknown</SectionTitle>
                                                <Body>Unknown</Body>
                                            </>
                                        );
                                    }),
                                )}
                            </div>
                        );
                    }),
                    (items) =>
                        items.length > 0 || !benchmarkRanked ? (
                            items
                        ) : (
                            <Body>
                                None of the constructs measured for your school this year scored above the FFI
                                benchmark, so no meaningful strengths are listed. The domain sections of this report
                                show how close each construct is.
                            </Body>
                        ),
                )}
            </div>
        </div>
    );
};
